import React from 'react';
import ReactDOM from 'react-dom';
import { ChartTooltip, ChartTooltipColors } from '@components/ChartTooltip/ChartTooltip.component';
import { PlcColor, PlcFonts } from '@styles/colors';
import { Spacing } from '@styles/Spacing.style';
import { formatDateCompact, plcDayjs } from '@utils/date';
import { ChartDataSets, ChartOptions, ChartPoint, ChartTooltipModel } from 'chart.js';
import { ConfigType } from 'dayjs';

export type TimeChartData = Array<{ date: Date }>;

const TOOLTIP_ID = 'chartjs-tooltip';

export function getTimeChartXMin(data: TimeChartData) {
  return data.length > 0
    ? plcDayjs(data[0].date)
        .subtract(1, 'day')
        .toString()
    : undefined;
}

export function getTimeChartXMax(data: TimeChartData) {
  return data.length > 0
    ? plcDayjs(data[data.length - 1].date)
        .add(1, 'day')
        .toString()
    : undefined;
}

function getDatasetYValues(dataset: ChartDataSets) {
  return (dataset.data as ChartPoint[]).map(d => d.y) as number[];
}

export function getDatasetsMinOrMax(datasets: ChartDataSets[], method: 'min' | 'max') {
  return datasets.reduce((previous: number | undefined, dataset: ChartDataSets) => {
    const yValues = getDatasetYValues(dataset);
    const values = [previous, ...yValues].filter(v => typeof v === 'number' && Number.isFinite(v)) as number[];
    return Math[method](...values);
  }, undefined);
}

export function getTimeChartBounds(data: TimeChartData, datasets: ChartDataSets[]) {
  const xMin = getTimeChartXMin(data);
  const xMax = getTimeChartXMax(data);
  const minValue = getDatasetsMinOrMax(datasets, 'min');
  const maxValue = getDatasetsMinOrMax(datasets, 'max');
  const yMin = maxValue !== undefined && minValue !== undefined ? minValue - (maxValue - minValue) * 0.1 : undefined;
  const yMax = maxValue !== undefined && minValue !== undefined ? maxValue + (maxValue - minValue) * 0.1 : undefined;
  return {
    xMax,
    xMin,
    yMax,
    yMin,
  };
}

function getTooltipEl(opacity: number) {
  // Tooltip Element
  let tooltipEl = document.getElementById(TOOLTIP_ID);

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = TOOLTIP_ID;
    document.body.appendChild(tooltipEl);
  }
  tooltipEl.style.opacity = opacity.toString();
  tooltipEl.classList.add('plc-tooltip');
  tooltipEl.classList.remove('above', 'below', 'no-transform');
  tooltipEl.style.display = opacity === 0 ? 'none' : '';
  return tooltipEl;
}

export function buildTooltip(
  tooltipModel: ChartTooltipModel,
  unit: string,
  colors?: ChartTooltipColors,
  titleCallback = formatDateCompact
) {
  // @ts-ignore
  // eslint-disable-next-line no-underscore-dangle
  const datasets = this._chart && this._chart.data ? (this._chart.data.datasets as ChartDataSets[]) : undefined;
  const { dataPoints, opacity, caretX, caretY } = tooltipModel;
  const tooltipEl = getTooltipEl(opacity);

  if (!dataPoints || dataPoints.length === 0 || !datasets) {
    return;
  }

  const { index, label } = dataPoints[0];

  if (typeof index === 'undefined' || !label) {
    return;
  }
  // Be sure tooltip on the top of the max value #PLC-2344
  const y = dataPoints.reduce((previous, dataPoint) => {
    return dataPoint.y && dataPoint.y < previous ? dataPoint.y : previous;
  }, caretY);

  const tooltip = React.createElement(ChartTooltip, {
    colors,
    datasets,
    index,
    title: titleCallback(label),
    unit,
  });
  ReactDOM.render(tooltip, tooltipEl);

  // @ts-ignore
  // eslint-disable-next-line no-underscore-dangle
  const position = this._chart.canvas.getBoundingClientRect();
  const { width: tooltipWidth } = tooltipEl.getBoundingClientRect();
  let left = Math.max(tooltipWidth / 2, position.left + window.pageXOffset + caretX);
  left = Math.min(left, window.innerWidth - tooltipWidth / 2);
  const top = position.top + window.pageYOffset + y;

  // Display, position, and set styles for font
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.pointerEvents = 'none';
}

export function getTimeChartOptions(
  xMax: string | undefined,
  xMin: string | undefined,
  yMax: number | undefined,
  yMin: number | undefined,
  tooltipUnit = '',
  tooltipColors?: ChartTooltipColors,
  tooltipTitleCallback?: (d: ConfigType) => string
): ChartOptions {
  const axesLineColor = '#979797';
  return {
    legend: {
      display: false,
    },
    maintainAspectRatio: false,
    plugins: {
      // @ts-ignore
      datalabels: false,
    },
    responsive: true,
    scales: {
      xAxes: [
        {
          distribution: 'series',
          gridLines: {
            color: axesLineColor,
            drawOnChartArea: false,
            drawTicks: false,
            zeroLineColor: axesLineColor,
          },
          ticks: {
            fontColor: PlcColor.GREY_CLOUD_DARK,
            fontFamily: PlcFonts.Montserrat,
            max: xMax,
            min: xMin,
            padding: 24,
            source: 'labels',
          },
          type: 'time',
        },
      ],
      yAxes: [
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          afterTickToLabelConversion(scale: any) {
            if (scale.ticks.length > 0) {
              // Remove first y tick as it is calculate by us
              if (yMax) {
                // eslint-disable-next-line no-param-reassign
                scale.ticks[0] = null;
              }
              // Remove last y tick as it is calculate by us only if not 0
              if (yMin && scale.ticks[scale.ticks.length - 1] !== '0') {
                // eslint-disable-next-line no-param-reassign
                scale.ticks[scale.ticks.length - 1] = null;
              }
            }
          },
          gridLines: {
            borderDash: [1, 5],
            color: axesLineColor,
            drawTicks: false,
            zeroLineColor: axesLineColor,
          },
          ticks: {
            beginAtZero: false,
            fontColor: PlcColor.GREY_CLOUD_DARK,
            fontFamily: PlcFonts.Montserrat,
            max: yMax,
            min: yMin,
            padding: Spacing.m,
          },
        },
      ],
    },
    tooltips: {
      custom(tooltipModel) {
        const callback = buildTooltip.bind(this);
        callback(tooltipModel, tooltipUnit, tooltipColors, tooltipTitleCallback);
      },
      enabled: false,
      intersect: false,
      mode: 'index',
    },
  };
}

export function removeTooltip() {
  const tooltipEl = document.getElementById(TOOLTIP_ID);
  if (tooltipEl) {
    tooltipEl.remove();
  }
}
